import React from 'react';
import { IModifierTemplateItem, UserRoles } from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import DraggableSelectedItem from 'components/SelectedItem/DraggableSelectedItem';
import style from './DnDList.module.scss';

interface IDnDList {
	setModifiers: (value: string[]) => void;
	setModifiersWithAdditionalData: (value: IModifierTemplateItem[]) => void;
	modifiers: string[];
	modifiersWithAdditionalData: IModifierTemplateItem[];
}

export const DnDList: React.FC<IDnDList> = React.memo(
	({ setModifiers, setModifiersWithAdditionalData, modifiers, modifiersWithAdditionalData }) => {
		const hasChangePositionAccess = useRoleAccess([
			UserRoles.SUPER_USER,
			UserRoles.CLIENT_MANAGER,
			UserRoles.GROUP_MANAGER,
			UserRoles.TECHNICAL,
		]);

		const handleDragEnd = (result: DropResult) => {
			if (!modifiersWithAdditionalData.length || !result.destination) {
				return null;
			}
			const items = [...modifiersWithAdditionalData];
			const [reorderedItem] = items.splice(result.source.index, 1);
			items.splice(result.destination.index, 0, reorderedItem);
			setModifiersWithAdditionalData(items);
			setModifiers(items.map((el) => el.id));
		};

		const handleRemove = (id: string) => {
			const updatedModifiers = modifiers.filter((el) => el !== id);
			const updatedModifiersWithAdditionalData = modifiersWithAdditionalData.filter(
				(modifierWithAdditionalData) => modifierWithAdditionalData.id !== id,
			);
			setModifiers(updatedModifiers);
			setModifiersWithAdditionalData(updatedModifiersWithAdditionalData);
		};

		return (
			<DragDropContext onDragEnd={handleDragEnd}>
				<Droppable droppableId="modifierList">
					{(provided) => (
						<div className={style.groupsList} ref={provided.innerRef} {...provided.droppableProps}>
							{modifiersWithAdditionalData.map((el, index) => (
								<React.Fragment key={el.id}>
									<Draggable draggableId={el.id} index={index}>
										{(provided) => (
											<DraggableSelectedItem
												draggableInnerRef={provided.innerRef}
												provided={provided}
												positionNumber={index + 1}
												label={el.name || '-'}
												onDelete={() => handleRemove(el.id)}
												hasDragAccess={hasChangePositionAccess}
											/>
										)}
									</Draggable>
								</React.Fragment>
							))}
							{provided.placeholder}
						</div>
					)}
				</Droppable>
			</DragDropContext>
		);
	},
);
